import { Head } from "@inertiajs/react";
import Layout from "../Layout";
import { Button } from "@/components/ui/button";
import { ServiceCallToAction } from "@/components/marketing/ServiceCallToAction";
import { FAQ, type FAQItem } from "@/components/marketing/FAQ";
import { CALENDLY_COST_REVIEW_URL } from "@/lib/urls";
import {
  BarChart3,
  CheckCircle,
  Cloud,
  DollarSign,
  FileText,
  Gauge,
  HardDrive,
  Layers,
  Phone,
  Search,
  Server,
  Settings,
  Trash2,
  UserCheck,
  Wrench,
  Zap,
} from "lucide-react";

const TRUST_POINTS = [
  "Read-only AWS access",
  "Report ready to hand to your team",
  "No commitment",
] as const;

const CATEGORIES = [
  {
    icon: Gauge,
    title: "Rightsizing",
    description:
      "Instances, containers, and databases running larger than they need to be. We analyze real usage data to find rightsizing candidates with estimated monthly savings.",
    examples: "Oversized EC2, over-provisioned RDS, idle ECS tasks",
  },
  {
    icon: Wrench,
    title: "Legacy modernization",
    description:
      "Older resource configurations that cost more than their modern equivalents. Changes that reduce your bill without touching architecture.",
    examples: "EBS gp2 to gp3, previous-generation instances still running, classic load balancers",
  },
  {
    icon: Trash2,
    title: "Unused resources",
    description:
      "Resources running but not doing useful work. Orphaned volumes, idle load balancers, forgotten snapshots, and detached IPs adding to your bill.",
    examples: "Unattached EBS volumes, idle NAT gateways, orphaned snapshots",
  },
] as const;

const STEPS = [
  {
    icon: Phone,
    title: "Book a call",
    description:
      "We discuss your environment, goals, and what you want reviewed. No commitment.",
  },
  {
    icon: Search,
    title: "We scope the work",
    description:
      "Based on the size and complexity of your AWS environment, you get a quoted range of hours and a timeline.",
  },
  {
    icon: Settings,
    title: "We review",
    description:
      "Using read-only access and our own tooling, we go through your resources and find the savings. We'll help you set up a read-only access role. Takes about 5 minutes.",
  },
  {
    icon: FileText,
    title: "You get the report",
    description:
      "A prioritized list of findings with estimated savings, split by category. Ready for your team to implement.",
  },
] as const;

const SCOPE_OPTIONS = [
  {
    icon: Server,
    title: "Single service review",
    description:
      "Focused on one AWS service, like EC2 or RDS. Good when you know where the waste is but need an expert to quantify it and find the specifics.",
    detail: "Typically takes 3–6 hours",
    suited: "Teams with a known problem area",
  },
  {
    icon: Cloud,
    title: "Cloud-wide review",
    description:
      "A full sweep across your AWS account or organization. We look at compute, storage, databases, and networking.",
    detail: "Varies by environment size. We scope this on the call",
    suited: "Teams that haven't audited their cloud in a while",
  },
] as const;

const CREDIBILITY_ITEMS = [
  {
    icon: Zap,
    title: "Backed by real tooling",
    description:
      "Every review is powered by the same analysis engine behind the Infralyst platform. Findings are based on real usage data and pricing APIs, not estimates from a spreadsheet.",
  },
  {
    icon: UserCheck,
    title: "Led by the founders, every time",
    description:
      "The founders personally run every review. You work directly with the people who built the analysis engine, not a junior consultant reading a runbook.",
  },
  {
    icon: BarChart3,
    title: "Broader than our tools",
    description:
      "We review your full AWS footprint, not just what our platform covers. You get human judgment across everything.",
  },
  {
    icon: DollarSign,
    title: "Transparent billing",
    description:
      "You get a quoted range of hours upfront. We bill at $250/hour based on actual time spent. No retainers, no surprise invoices.",
  },
] as const;

function Hero(): React.ReactElement {
  return (
    <section className="px-4 py-16 pt-40">
      <div className="max-w-4xl mx-auto text-center">
        <p className="text-sm font-semibold tracking-widest uppercase text-primary mb-4">
          One-off service
        </p>
        <h1 className="mb-6 text-5xl font-bold leading-tight tracking-tight md:text-6xl">
          AWS <span className="text-primary">cost review</span>, delivered as
          a report
        </h1>
        <p className="max-w-2xl mx-auto mb-8 text-xl leading-relaxed text-surface-foreground/80">
          We review your AWS environment, find savings across rightsizing,
          legacy resources, and unused infrastructure, and hand you a
          prioritized report your team can act on.
        </p>
        <div className="flex items-center justify-center mb-6">
          <Button asChild size="lg">
            <a
              href={CALENDLY_COST_REVIEW_URL}
              target="_blank"
              rel="noopener noreferrer"
            >
              Book a free call
            </a>
          </Button>
        </div>
        <ul className="flex flex-col gap-2 items-center">
          {TRUST_POINTS.map((point) => (
            <li key={point} className="flex items-center gap-2 text-sm text-surface-foreground/60">
              <CheckCircle className="size-4 text-green-500 flex-none" />
              {point}
            </li>
          ))}
        </ul>
      </div>
    </section>
  );
}

function PricingAndScope(): React.ReactElement {
  return (
    <section className="py-16 px-4">
      <div className="max-w-4xl mx-auto">
        <h2 className="text-4xl font-bold text-center mb-4">
          <span className="text-primary">Pricing</span> and scope
        </h2>
        <p className="text-center text-surface-foreground/80 mb-12 max-w-2xl mx-auto">
          $250/hour, billed on actual time spent. We quote the hours upfront so
          you know what to expect before we start.
        </p>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {SCOPE_OPTIONS.map(({ icon: Icon, title, description, detail, suited }) => (
            <div
              key={title}
              className="p-6 rounded-3xl border bg-background/50 hover:border-primary/20 hover:bg-primary/5 transition-colors flex flex-col"
            >
              <div className="size-12 rounded-full bg-primary/10 text-primary flex items-center justify-center mb-4">
                <Icon className="size-6" />
              </div>
              <h3 className="text-xl font-bold mb-2">{title}</h3>
              <p className="text-sm text-surface-foreground/80 leading-relaxed mb-4 flex-1">
                {description}
              </p>
              <p className="text-sm font-medium text-primary mb-1">
                {detail}
              </p>
              <p className="text-xs text-surface-foreground/60">
                Best for: {suited}
              </p>
            </div>
          ))}
        </div>
        <p className="text-sm text-surface-foreground/60 text-center mt-8">
          Not sure which scope fits? We&apos;ll help you decide on the call.
        </p>
      </div>
    </section>
  );
}

function WhatYouGet(): React.ReactElement {
  return (
    <section className="py-16 px-4">
      <div className="max-w-4xl mx-auto">
        <h2 className="text-4xl font-bold text-center mb-4">
          What you <span className="text-primary">get</span>
        </h2>
        <p className="text-center text-surface-foreground/80 mb-12 max-w-2xl mx-auto">
          You receive a prioritized PDF report with every finding ranked by
          estimated monthly savings. Each item includes what to change, why it
          saves money, and the risk level so your team can plan confidently.
        </p>
        <div className="rounded-3xl border bg-background/50 p-8 md:p-10">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            <div className="flex flex-col items-center text-center">
              <div className="size-12 rounded-full bg-primary/10 text-primary flex items-center justify-center mb-4">
                <Layers className="size-6" />
              </div>
              <h3 className="text-lg font-bold mb-1">Categorized findings</h3>
              <p className="text-sm text-surface-foreground/80">
                Split into rightsizing, modernization, and unused resources
              </p>
            </div>
            <div className="flex flex-col items-center text-center">
              <div className="size-12 rounded-full bg-primary/10 text-primary flex items-center justify-center mb-4">
                <BarChart3 className="size-6" />
              </div>
              <h3 className="text-lg font-bold mb-1">Prioritized by impact</h3>
              <p className="text-sm text-surface-foreground/80">
                Highest savings at the top so you know where to start
              </p>
            </div>
            <div className="flex flex-col items-center text-center">
              <div className="size-12 rounded-full bg-primary/10 text-primary flex items-center justify-center mb-4">
                <HardDrive className="size-6" />
              </div>
              <h3 className="text-lg font-bold mb-1">Implementation ready</h3>
              <p className="text-sm text-surface-foreground/80">
                Specific resource IDs, recommended changes, and risk notes
              </p>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

function Categories(): React.ReactElement {
  return (
    <section className="py-16 px-4">
      <div className="max-w-5xl mx-auto">
        <h2 className="text-4xl font-bold text-center mb-4">
          Three areas we <span className="text-primary">always check</span>
        </h2>
        <p className="text-center text-surface-foreground/80 mb-12 max-w-2xl mx-auto">
          Most AWS environments have savings hiding in all three. You can
          request a review focused on one category or let us cover everything.
        </p>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
          {CATEGORIES.map(({ icon: Icon, title, description, examples }) => (
            <div
              key={title}
              className="p-6 rounded-3xl border bg-background/50 hover:border-primary/20 hover:bg-primary/5 transition-colors flex flex-col"
            >
              <div className="size-12 rounded-full bg-primary/10 text-primary flex items-center justify-center mb-4">
                <Icon className="size-6" />
              </div>
              <h3 className="text-xl font-bold mb-2">{title}</h3>
              <p className="text-sm text-surface-foreground/80 leading-relaxed mb-4 flex-1">
                {description}
              </p>
              <p className="text-xs text-surface-foreground/60">
                e.g. {examples}
              </p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

function HowItWorks(): React.ReactElement {
  return (
    <section className="py-16 px-4">
      <div className="max-w-4xl mx-auto">
        <h2 className="text-4xl font-bold text-center mb-12">
          How it <span className="text-primary">works</span>
        </h2>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
          {STEPS.map(({ icon: Icon, title, description }) => (
            <div key={title} className="flex gap-4">
              <div className="size-12 rounded-full bg-primary/10 text-primary flex items-center justify-center flex-shrink-0">
                <Icon className="size-6" />
              </div>
              <div>
                <h3 className="text-lg font-bold mb-1">{title}</h3>
                <p className="text-sm text-surface-foreground/80 leading-relaxed">
                  {description}
                </p>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

function WhyInfralyst(): React.ReactElement {
  return (
    <section className="py-16 px-4">
      <div className="max-w-5xl mx-auto">
        <h2 className="text-4xl font-bold text-center mb-12">
          Why <span className="text-primary">Infralyst</span>
        </h2>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
          {CREDIBILITY_ITEMS.map(({ icon: Icon, title, description }) => (
            <div key={title} className="flex gap-4">
              <div className="size-12 rounded-full bg-primary/10 text-primary flex items-center justify-center flex-shrink-0">
                <Icon className="size-6" />
              </div>
              <div>
                <h3 className="text-lg font-bold mb-1">{title}</h3>
                <p className="text-sm text-surface-foreground/80 leading-relaxed">
                  {description}
                </p>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

const COST_REVIEW_FAQ_ITEMS: readonly FAQItem[] = [
  {
    question:
      "How does a one-off AWS cost review compare to running a cost optimization tool full-time?",
    answer:
      "A cost optimization platform monitors continuously but only covers what it's built to detect. Infralyst's cost review is a manual, one-off inspection of your full AWS footprint. It covers services and configurations no automated tool checks. Teams often use a one-off review to find the big wins, then decide whether ongoing tooling is worth the investment.",
  },
  {
    question:
      "What can a manual AWS cost review find that automated tools miss?",
    answer:
      "Automated tools cover what they're built to detect. A manual review checks everything: idle resources, legacy configurations, oversized databases, unattached volumes, NAT gateway overuse, and service-specific pricing mistakes. Infralyst's cost review analyzes real utilization data and pricing APIs to produce a prioritized list of specific changes with estimated monthly savings attached to each one.",
  },
  {
    question:
      "What's the difference between a single service review and a cloud-wide review?",
    answer:
      "A single service review focuses on one AWS service, like EC2 or RDS, and typically takes 3 to 6 hours. A cloud-wide review covers compute, storage, databases, and networking across your account or organization. Infralyst scopes the cloud-wide review on an introductory call based on your environment size.",
  },
  {
    question:
      "How does Infralyst's cost review compare to hiring a cloud consultant?",
    answer:
      "Most consultancies staff junior engineers working from checklists. Infralyst's founders run every review personally, applying the same methodology and thresholds behind the Infralyst platform but covering more ground. Findings are backed by real CloudWatch data and AWS pricing APIs, not estimates from a spreadsheet. Billing is transparent at $250/hour with no retainer.",
  },
  {
    question: "What does the Infralyst cost review report include?",
    answer:
      "You get a prioritized PDF with every finding ranked by estimated monthly savings. Each item includes the specific resource ID, what to change, why it saves money, and the risk level. Findings are split into three categories: rightsizing, legacy modernization, and unused resources.",
  },
  {
    question:
      "What AWS services does Infralyst check during a cost review?",
    answer:
      "Every service you use. The review covers your full AWS footprint, not a fixed checklist. If you're running it and paying for it, Infralyst looks at it. Common findings span compute, storage, databases, networking, and supporting services like NAT gateways, Elastic IPs, and snapshots.",
  },
  {
    question:
      "How does Infralyst find savings during a cost review?",
    answer:
      "Infralyst pulls real CloudWatch utilization data and cross-references it with live AWS pricing. The methodology uses the same thresholds and quality gates behind the Infralyst platform, but the review itself is manual and broader. It covers everything in your account, not just what automated tooling covers.",
  },
  {
    question: "What AWS access does Infralyst need for a cost review?",
    answer:
      "Read-only. Infralyst uses a read-only IAM role to pull CloudWatch metrics and resource metadata. It cannot stop, start, modify, or delete anything in your account. Setting up the role takes about 5 minutes, and Infralyst walks you through it.",
  },
  {
    question: "What happens after I receive the cost review report?",
    answer:
      "The report is yours. Your team decides what to implement and when. Each finding includes the specific change, the estimated savings, and a risk assessment so you can prioritize confidently. If you want continuous monitoring after the review, the Infralyst platform covers EC2, ASG, and ECS Fargate rightsizing, plus EBS gp2 to gp3 migration.",
  },
  {
    question:
      "Is there any commitment or lock-in with the cost review?",
    answer:
      "None. You book a free introductory call, get a quoted range of hours, and only pay for actual time spent at $250/hour. There are no retainers, subscriptions, or surprise invoices. If you decide not to proceed after the scoping call, you owe nothing.",
  },
] as const;

const SERVICE_JSON_LD = JSON.stringify({
  "@context": "https://schema.org",
  "@type": "Service",
  name: "AWS Cost Review",
  description:
    "One-off AWS cost optimization review. We find rightsizing, modernization, and unused resource savings and deliver a prioritized report your team can act on.",
  url: "https://infralyst.io/services/cost-review",
  provider: {
    "@type": "Organization",
    name: "Infralyst",
    url: "https://infralyst.io",
  },
  offers: {
    "@type": "Offer",
    price: "250",
    priceCurrency: "USD",
    description: "$250/hour AWS cost review",
  },
});

export default function CostReview(): React.ReactElement {
  return (
    <Layout
      title="AWS Cost Review"
      description="One-off AWS cost optimization review. We find rightsizing, modernization, and unused resource savings and deliver a prioritized report your team can act on."
    >
      <Head>
        <script type="application/ld+json">{SERVICE_JSON_LD}</script>
      </Head>
      <Hero />
      <Categories />
      <WhatYouGet />
      <HowItWorks />
      <PricingAndScope />
      <WhyInfralyst />
      <FAQ
        items={COST_REVIEW_FAQ_ITEMS}
        title="AWS Cost Review FAQ"
        highlightWord="Cost Review"
        className="py-16 px-4"
      />
      <ServiceCallToAction />
    </Layout>
  );
}
